import React, { useState } from "react";
import { Theme } from "../../types";
import { SettingRow } from "./SettingRow";
import { ToggleSwitch } from "./ToggleSwitch";
import { ThemeButton } from "../common/ThemeButton";
import { TextSizeButton } from "../common/TextSizeButton";
import { ActivityLayoutButton } from "../common/ActivityLayoutButton";
import debugx from "debugx";

const debug = debugx("bikedash:settings");

interface GeneralTabProps {
  userPreferences: any;
  updateUserPreferences: (updates: any) => void;
  setApiKey: (provider: string, key: string) => void;
}

export function GeneralTab({
  userPreferences,
  updateUserPreferences,
  setApiKey,
}: GeneralTabProps) {
  const [thunderforestKey, setThunderforestKey] = useState(
    userPreferences.apiKeys?.thunderforest || "",
  );

  const handleThemeChange = (theme: Theme) => {
    debug("Changing theme to:", theme);
    updateUserPreferences({ theme });
  };

  const handleUnitsChange = (units: "metric" | "imperial") => {
    debug("Changing units to:", units);
    updateUserPreferences({ units });
  };

  // Check for API key from cached auth first, then manual override
  const cachedAuth = localStorage.getItem("bikedash_cached_auth");
  let hasApiKey = false;
  if (cachedAuth) {
    try {
      const cached = JSON.parse(cachedAuth);
      hasApiKey = !!cached.thunderforestApiKey;
    } catch (e) {
      // Ignore
    }
  }
  // Fallback to manual override
  if (!hasApiKey) {
    hasApiKey = !!userPreferences.apiKeys?.thunderforest;
  }

  return (
    <div className="settings-section">
      {/* General Settings */}
      <div className="settings-group-header">Appearance</div>

      <div className="setting-group">
        <label>Theme</label>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(5, 1fr)",
            gap: "8px",
            marginBottom: "8px",
            height: "70px",
          }}
        >
          <ThemeButton
            theme="auto"
            isSelected={userPreferences.theme === "auto"}
            onSelect={handleThemeChange}
          />
          <ThemeButton
            theme="dark"
            isSelected={userPreferences.theme === "dark"}
            onSelect={handleThemeChange}
          />
          <ThemeButton
            theme="light"
            isSelected={userPreferences.theme === "light"}
            onSelect={handleThemeChange}
          />
          <ThemeButton
            theme="high-contrast-dark"
            isSelected={userPreferences.theme === "high-contrast-dark"}
            onSelect={handleThemeChange}
          />
          <ThemeButton
            theme="high-contrast-light"
            isSelected={userPreferences.theme === "high-contrast-light"}
            onSelect={handleThemeChange}
          />
        </div>
      </div>

      <div className="setting-group">
        <label>Text size</label>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: "8px",
            height: "60px",
          }}
        >
          <TextSizeButton
            size="small"
            isSelected={(userPreferences.textSize ?? "medium") === "small"}
            onSelect={(size) => updateUserPreferences({ textSize: size })}
          />
          <TextSizeButton
            size="medium"
            isSelected={(userPreferences.textSize ?? "medium") === "medium"}
            onSelect={(size) => updateUserPreferences({ textSize: size })}
          />
          <TextSizeButton
            size="large"
            isSelected={(userPreferences.textSize ?? "medium") === "large"}
            onSelect={(size) => updateUserPreferences({ textSize: size })}
          />
        </div>
      </div>

      <div className="setting-group">
        <label>Activity bar layout</label>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: "8px",
            height: "120px",
          }}
        >
          <ActivityLayoutButton
            layout="expanded"
            isSelected={
              (userPreferences.activityBarLayout ?? "expanded") === "expanded"
            }
            onSelect={(layout) =>
              updateUserPreferences({ activityBarLayout: layout })
            }
          />
          <ActivityLayoutButton
            layout="compact"
            isSelected={userPreferences.activityBarLayout === "compact"}
            onSelect={(layout) =>
              updateUserPreferences({ activityBarLayout: layout })
            }
          />
          <ActivityLayoutButton
            layout="overlay"
            isSelected={userPreferences.activityBarLayout === "overlay"}
            onSelect={(layout) =>
              updateUserPreferences({ activityBarLayout: layout })
            }
          />
        </div>
      </div>

      <div className="setting-group">
        <label>Units</label>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "8px",
          }}
        >
          <button
            onClick={() => handleUnitsChange("metric")}
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              padding: "16px 12px",
              background:
                userPreferences.units === "metric"
                  ? "rgba(62, 106, 225, 0.3)"
                  : "var(--bg-subtle)",
              border:
                userPreferences.units === "metric"
                  ? "2px solid var(--accent)"
                  : "1px solid var(--border)",
              borderRadius: "8px",
              cursor: "pointer",
              transition: "all 0.2s",
            }}
          >
            <div
              style={{
                fontSize: "14px",
                color:
                  userPreferences.units === "metric"
                    ? "var(--text-primary)"
                    : "var(--text-secondary)",
                fontWeight: userPreferences.units === "metric" ? 600 : 500,
                marginBottom: "4px",
              }}
            >
              Metric
            </div>
            <div
              style={{
                fontSize: "11px",
                color: "var(--text-secondary)",
              }}
            >
              km, km/h
            </div>
          </button>
          <button
            onClick={() => handleUnitsChange("imperial")}
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              padding: "16px 12px",
              background:
                userPreferences.units === "imperial"
                  ? "rgba(62, 106, 225, 0.3)"
                  : "var(--bg-subtle)",
              border:
                userPreferences.units === "imperial"
                  ? "2px solid var(--accent)"
                  : "1px solid var(--border)",
              borderRadius: "8px",
              cursor: "pointer",
              transition: "all 0.2s",
            }}
          >
            <div
              style={{
                fontSize: "14px",
                color:
                  userPreferences.units === "imperial"
                    ? "var(--text-primary)"
                    : "var(--text-secondary)",
                fontWeight: userPreferences.units === "imperial" ? 600 : 500,
                marginBottom: "4px",
              }}
            >
              Imperial
            </div>
            <div
              style={{
                fontSize: "11px",
                color: "var(--text-secondary)",
              }}
            >
              mi, mph
            </div>
          </button>
        </div>
      </div>

      <SettingRow
        label="Keep screen on"
        description="Prevent screen from sleeping during activities"
        checked={!!userPreferences.keepScreenOn}
        onChange={(checked) => {
          debug("Keep screen on toggled to: %s", checked);
          debug(
            "Current userPreferences.keepScreenOn: %s",
            userPreferences.keepScreenOn,
          );
          updateUserPreferences({ keepScreenOn: checked });
        }}
      />

      {/* Privacy Settings */}
      <div className="settings-group-header">Privacy</div>

      <div className="setting-group">
        <label>Default activity privacy</label>
        <select
          value={userPreferences.defaultActivityPrivacy}
          onChange={(e) =>
            updateUserPreferences({
              defaultActivityPrivacy: e.target.value as
                | "private"
                | "friends"
                | "public",
            })
          }
        >
          <option value="private">Private</option>
          <option value="friends">Friends only</option>
          <option value="public">Public</option>
        </select>
      </div>

      <SettingRow
        label="Share location"
        description="Let friends see your real-time location"
        checked={userPreferences.shareLocationWithFriends || false}
        onChange={(checked) =>
          updateUserPreferences({ shareLocationWithFriends: checked })
        }
      />

      <div className="setting-row">
        <div className="setting-info">
          <div className="setting-label">Proximity alerts</div>
          <p className="setting-description">Notify when friends are nearby</p>
        </div>
        <ToggleSwitch
          checked={userPreferences.proximityAlertsEnabled || false}
          onChange={(checked) =>
            updateUserPreferences({ proximityAlertsEnabled: checked })
          }
        />
        <div
          className="slider-controls"
          style={{
            display: "flex",
            alignItems: "center",
            gap: "var(--spacing-md)",
          }}
        >
          <input
            type="range"
            min="1"
            max="10"
            value={(userPreferences.proximityRadius || 5000) / 1000}
            onChange={(e) => {
              const value = Number(e.target.value) * 1000;
              updateUserPreferences({ proximityRadius: value });
            }}
            style={{
              width: "100px",
              opacity: userPreferences.proximityAlertsEnabled ? 1 : 0.3,
              pointerEvents: userPreferences.proximityAlertsEnabled
                ? "auto"
                : "none",
            }}
            className="opacity-slider"
          />
          <span
            style={{
              fontSize: "12px",
              color: "var(--text-secondary)",
              minWidth: "35px",
              opacity: userPreferences.proximityAlertsEnabled ? 1 : 0.3,
            }}
          >
            {((userPreferences.proximityRadius || 5000) / 1000).toFixed(0)}km
          </span>
        </div>
      </div>

      {/* Access & Sync Settings */}
      <div className="settings-group-header">Access & Sync</div>

      <div className="setting-group">
        <label>Thunderforest Maps</label>
        <p className="setting-description">
          Premium map styles are automatically available when you log in with
          your account.
        </p>
        <div
          style={{
            padding: "12px",
            background: "var(--bg-subtle)",
            borderRadius: "8px",
            marginTop: "12px",
          }}
        >
          {hasApiKey ? (
            <div style={{ color: "var(--accent)" }}>
              ✓ API key available - Thunderforest maps are enabled
            </div>
          ) : (
            <div style={{ color: "var(--text-secondary)" }}>
              ⚠️ No API key - Log in to access Thunderforest maps
            </div>
          )}
        </div>

        {/* Optional manual override */}
        <details style={{ marginTop: "20px" }}>
          <summary
            style={{
              cursor: "pointer",
              color: "var(--text-secondary)",
              fontSize: "12px",
            }}
          >
            Advanced: Manual API key override
          </summary>
          <div style={{ marginTop: "12px" }}>
            <p className="setting-description" style={{ fontSize: "11px" }}>
              Override the server-provided API key (for testing only). Changes
              are saved automatically.
            </p>
            <div
              style={{
                display: "flex",
                gap: "8px",
                alignItems: "center",
              }}
            >
              <input
                type="text"
                placeholder="Enter API key (or leave empty to remove)"
                value={thunderforestKey}
                onChange={(e) => {
                  setThunderforestKey(e.target.value);
                  setApiKey("thunderforest", e.target.value);
                  if (e.target.value) {
                    debug("Thunderforest API key saved");
                  } else {
                    debug("Thunderforest API key override removed");
                  }
                }}
                style={{
                  flex: 1,
                  padding: "6px",
                  fontSize: "11px",
                  fontFamily: "monospace",
                  background: "rgba(255, 255, 255, 0.1)",
                  border: "1px solid rgba(255, 255, 255, 0.2)",
                  borderRadius: "4px",
                  color: "var(--text-primary)",
                }}
              />
              <button
                onClick={() => {
                  setThunderforestKey("");
                  setApiKey("thunderforest", "");
                  debug("Thunderforest API key override removed");
                }}
                className="control-btn"
                style={{ padding: "6px 12px", fontSize: "11px" }}
              >
                {thunderforestKey ? "Clear" : "Default"}
              </button>
            </div>
            {userPreferences.apiKeys?.thunderforest && (
              <div
                style={{
                  marginTop: "8px",
                  fontSize: "11px",
                  color: "var(--accent)",
                }}
              >
                ✓ Manual override active
              </div>
            )}
          </div>
        </details>
      </div>

      <SettingRow
        label="Offline mode"
        description="Disable server synchronization"
        checked={userPreferences.offlineMode || false}
        onChange={(checked) => updateUserPreferences({ offlineMode: checked })}
      />

      <SettingRow
        label="Auto-sync"
        description="Sync data when connection available"
        checked={userPreferences.autoSync || false}
        onChange={(checked) => updateUserPreferences({ autoSync: checked })}
      />

      <SettingRow
        label="Auto-update"
        description="Automatically prepare app updates in background (won't reload until you reopen the app)"
        checked={userPreferences.autoUpdate ?? true}
        onChange={(checked) => {
          debug("Auto-update toggled to: %s", checked);
          updateUserPreferences({ autoUpdate: checked });
        }}
      />

      {/* Camera Settings */}
      <div className="settings-group-header">Camera</div>

      <SettingRow
        label="Enable camera"
        description="Show camera button in header (configure camera source using the camera button)"
        checked={userPreferences.cameraEnabled || false}
        onChange={(checked) => {
          debug("Camera enabled toggled to: %s", checked);
          updateUserPreferences({ cameraEnabled: checked });
        }}
      />
    </div>
  );
}
